import { useEffect, useMemo, useState } from 'react'
import { useDataSource } from '../lib/dataSource'
import { computeReminderSchedule, reminderSilence, type ScheduledReminder } from '../lib/reminders/schedule'
import { REMINDER_DEFAULT_OFFSET, REMINDER_DEFAULT_OFFSET_KEY, offsetMinutesOf, type ReminderDefaultOffset } from '../lib/reminders/preference'

const HOUR = 60 * 60 * 1000

/** Die Uhr, die der Zeitplan braucht: Sie rückt stündlich weiter, damit sein Fenster mitwandert. */
function useHourlyNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), HOUR)
    return () => clearInterval(timer)
  }, [])
  return now
}

export interface ReminderSchedule {
  entries: ScheduledReminder[]
  /** Aufgaben mit Erinnerungen, die noch offen sind — eine gezeigte Mitteilung der übrigen geht zu. */
  openTaskIds: string[]
}

/**
 * **Der Zeitplan aller Erinnerungen** (Plan task-reminders, Etappe 2) — neu gerechnet bei jeder
 * Änderung an Erinnerungen, ihren Aufgaben oder dem Standard-Vorlauf, auch wenn sie per Sync
 * kommt, und jede Stunde. Die Live-Abfrage statt einzelner Aufrufstellen: Sync-Pull und
 * Kaskaden schreiben an der DataSource vorbei, die Abfrage sieht sie trotzdem.
 * `undefined`, bis alles geladen ist.
 */
export function useReminderSchedule(): ReminderSchedule | undefined {
  const source = useDataSource()
  const reminders = source.useLiveReminders()
  const taskIds = useMemo(() => [...new Set((reminders ?? []).map(r => r.taskId))].sort(), [reminders])
  const chains = source.useTaskChains(taskIds)
  const [offset, , offsetReady] = source.usePreference<ReminderDefaultOffset>(REMINDER_DEFAULT_OFFSET_KEY, REMINDER_DEFAULT_OFFSET)
  const now = useHourlyNow()
  return useMemo(() => {
    if (!reminders || !chains || !offsetReady) return undefined
    const defaultOffsetMinutes = offsetMinutesOf(offset)
    const closed = new Set(['done', 'deleted'])
    const openTaskIds = [...new Set(reminders
      .filter(r => !closed.has(reminderSilence(r, chains.get(r.taskId), defaultOffsetMinutes) ?? ''))
      .map(r => r.taskId))]
    return { entries: computeReminderSchedule({ reminders, chains, defaultOffsetMinutes, now }), openTaskIds }
  }, [reminders, chains, offset, offsetReady, now])
}
